import { useState, useEffect } from 'react';
import { Calendar, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const BookingCalendar = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);

  // Generate next 30 days as available dates (excluding Sundays)
  useEffect(() => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      // Skip Sundays (0) 
      if (date.getDay() !== 0) {
        dates.push(date);
      }
    }
    setAvailableDates(dates);
  }, []);

  // Available time slots
  const timeSlots = [
    '10:00 AM', '11:00 AM', '12:00 PM',
    '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const isDateAvailable = (day) => {
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return availableDates.some(d => 
      d.getDate() === checkDate.getDate() &&
      d.getMonth() === checkDate.getMonth() &&
      d.getFullYear() === checkDate.getFullYear()
    );
  };

  const handleDateSelect = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (isDateAvailable(day)) {
      setSelectedDate(date);
      setSelectedTime(null);
    }
  };

  const handleBooking = () => {
    if (selectedDate && selectedTime) {
      // Store booking details
      localStorage.setItem('pranaBooking', JSON.stringify({
        date: selectedDate.toISOString(),
        time: selectedTime
      }));
      // Navigate to auth/booking
      navigate('/auth');
    }
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 backdrop-blur-xl border-2 border-purple-400/30 rounded-3xl p-8 shadow-2xl">
      <div className="text-center mb-8">
        <div className="inline-block bg-cyan-500/20 px-6 py-2 rounded-full mb-4 border border-cyan-400/40">
          <Calendar className="w-5 h-5 inline mr-2 text-cyan-300" />
          <span className="text-cyan-300 font-bold text-sm">BOOK YOUR PRANA ANALYSIS</span>
        </div>
        <h3 className="text-3xl lg:text-4xl font-bold text-white mb-2" style={{fontFamily: 'Cormorant Garamond, serif'}}>
          Schedule Your First Session
        </h3>
        <p className="text-purple-200">Select a date and time for your Prana Analysis (₹2,500 for 2 sessions)</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Calendar */}
        <div className="bg-purple-900/30 backdrop-blur-lg rounded-2xl p-6 border border-purple-400/20">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="text-purple-300 hover:text-white transition-colors"
            >
              ‹
            </button>
            <h4 className="text-lg font-semibold text-white">{monthName}</h4>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="text-purple-300 hover:text-white transition-colors"
            >
              ›
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs font-semibold text-purple-300 mb-2">
                {day}
              </div>
            ))}
            
            {/* Empty cells for days before month starts */}
            {[...Array(startingDayOfWeek)].map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            
            {/* Days of month */}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const available = isDateAvailable(day);
              const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
              const isSelected = selectedDate && 
                selectedDate.getDate() === day &&
                selectedDate.getMonth() === currentMonth.getMonth() &&
                selectedDate.getFullYear() === currentMonth.getFullYear();

              return (
                <button
                  key={day}
                  onClick={() => handleDateSelect(day)}
                  disabled={!available}
                  className={`
                    aspect-square rounded-lg text-sm font-medium transition-all
                    ${available ? 'hover:bg-cyan-500/30 cursor-pointer' : 'opacity-30 cursor-not-allowed'}
                    ${isSelected ? 'bg-cyan-500 text-white' : available ? 'bg-purple-800/50 text-white' : 'bg-purple-900/20 text-purple-400'}
                  `}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slots */}
        <div className="bg-purple-900/30 backdrop-blur-lg rounded-2xl p-6 border border-purple-400/20">
          <div className="flex items-center mb-6">
            <Clock className="w-5 h-5 text-cyan-300 mr-2" />
            <h4 className="text-lg font-semibold text-white">Select Time Slot</h4>
          </div>

          {!selectedDate ? (
            <p className="text-purple-300 text-center py-8">Please select a date first</p>
          ) : (
            <div className="space-y-3">
              {timeSlots.map(time => (
                <button
                  key={time}
                  onClick={() => setSelectedTime(time)}
                  className={`
                    w-full py-3 px-4 rounded-xl font-medium transition-all
                    ${selectedTime === time 
                      ? 'bg-cyan-500 text-white shadow-lg' 
                      : 'bg-purple-800/50 text-white hover:bg-purple-700/50'}
                  `}
                >
                  {time}
                </button>
              ))}
            </div>
          )}

          {/* Selected Summary */}
          {selectedDate && selectedTime && (
            <div className="mt-6 p-4 bg-cyan-500/10 border-2 border-cyan-400/30 rounded-xl">
              <div className="flex items-start space-x-3">
                <CheckCircle2 className="w-5 h-5 text-cyan-300 mt-1" />
                <div>
                  <p className="text-white font-semibold mb-1">Booking Summary</p>
                  <p className="text-cyan-200 text-sm">
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  <p className="text-cyan-200 text-sm">{selectedTime}</p>
                  <p className="text-yellow-300 text-sm font-semibold mt-2">₹2,500 (2 Sessions)</p>
                </div>
              </div>
              
              <Button
                onClick={handleBooking}
                className="w-full mt-4 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-3 rounded-xl font-semibold shadow-lg"
                style={{boxShadow: '0 0 30px rgba(0, 217, 255, 0.4)'}}
              >
                Confirm Booking
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookingCalendar;
